import { api, unwrap, type APIResponse, type Paginated } from "./client";

// Mirrors app/routers/did.py DIDOut.
export interface Did {
  did_id: number;
  name: string;
  phone: string;
  created_by: number | null;
  is_quarantined: boolean;
  is_retired: boolean;
  first_used_at: string | null;
  did_group_id: number | null;
  did_group_name: string | null;
  is_blocked: boolean;
  block_reason: string | null;
  blocked_until: string | null;
  block_count: number;
  action_type: string | null;
  action_data: string | null;
  inbound_mode?: string | null;
}

// Mirrors DIDGroupOut.
export interface DidGroup {
  did_group_id: number;
  name: string;
  created_by: number | null;
  member_count: number;
  active_count?: number;
  quarantined_count?: number;
  inbound_action_type?: string | null;
  inbound_action_data?: string | null;
  inbound_mode?: string | null;
}

// Mirrors DIDGroupDetailOut.
export interface DidGroupDetail {
  did_group_id: number;
  name: string;
  created_by: number | null;
  created_at: string;
  inbound_action_type?: string | null;
  inbound_action_data?: string | null;
  inbound_mode?: string | null;
  members: Did[];
}

// Mirrors CarrierBlockOut.
export interface CarrierBlock {
  dest_carrier: string;
  reason: string;
  blocked_until: string | null;
  last_block: string | null;
  block_count: number;
}

// Mirrors DIDReputationOut.
export interface DidReputation {
  did_id: number;
  phone: string;
  is_quarantined: boolean;
  is_retired: boolean;
  hourly_cap: number;
  daily_cap: number;
  warmup_active: boolean;
  age_days: number | null;
  carrier_blocks: CarrierBlock[];
  recipient_cooldown_count: number;
  attempts_7d: number;
  connected_7d: number;
  connect_rate: number;
  sip603_rate: number;
  fast_hangup_rate: number;
}

export interface DidListParams {
  page?: number;
  per_page?: number;
  include_retired?: boolean;
  did_group_id?: number;
  unassigned?: boolean;
  q?: string;
  status_filter?: "active" | "quarantined" | "blocked" | "retired";
}

export async function listDids(params: DidListParams): Promise<Paginated<Did>> {
  const resp = await api.get<Paginated<Did>>("/dids/", { params });
  if (!resp.data.success) throw new Error(resp.data.error || "Failed to load DIDs");
  return resp.data;
}

export async function createDid(body: {
  name?: string;
  phone: string;
  did_group_id?: number | null;
}): Promise<number> {
  const resp = await api.post<APIResponse<{ did_id: number }>>("/dids/", body);
  return unwrap(resp.data).did_id;
}

export interface BulkCreateResult {
  created: number;
  did_ids: number[];
  skipped_duplicates: string[];
  invalid: string[];
}

export async function bulkCreateDids(body: {
  phones: string[];
  name_prefix?: string;
  did_group_id?: number | null;
}): Promise<BulkCreateResult> {
  const resp = await api.post<APIResponse<BulkCreateResult>>("/dids/bulk-create", body);
  return unwrap(resp.data);
}

export interface ProvisionResult {
  order_id: string | null;
  provisioned: string[];
  count: number;
  charged: string;
  uncharged_count: number;
}

export async function provisionDids(body: {
  area_code: string;
  quantity: number;
  did_group_id?: number | null;
}): Promise<ProvisionResult> {
  const resp = await api.post<APIResponse<ProvisionResult>>("/dids/provision", body);
  return unwrap(resp.data);
}

export async function bulkMoveDids(body: {
  did_ids: number[];
  target_group_id: number | null;
  apply_group_forwarding?: boolean;
}): Promise<{ moved: number; target_group_id: number | null }> {
  const resp = await api.post<APIResponse<{ moved: number; target_group_id: number | null }>>(
    "/dids/bulk-move",
    body,
  );
  return unwrap(resp.data);
}

export async function bulkDidAction(body: {
  did_ids: number[];
  action: "quarantine" | "unquarantine" | "clear_blocks" | "retire" | "unretire";
}): Promise<{ affected: number; action: string }> {
  const resp = await api.post<APIResponse<{ affected: number; action: string }>>(
    "/dids/bulk-action",
    body,
  );
  return unwrap(resp.data);
}

export async function updateDid(
  didId: number,
  body: {
    name?: string;
    is_quarantined?: boolean;
    did_group_id?: number | null;
    action_type?: "agents" | "external" | "sip";
    action_data?: string;
    inbound_mode?: "direct" | "press1" | "press3";
  },
): Promise<void> {
  await api.put(`/dids/${didId}`, body);
}

export async function releaseDid(didId: number): Promise<void> {
  await api.delete(`/dids/${didId}`);
}

// ── Quarantine purge (release from carrier + soft-delete) ─────────────────

export interface QuarantinePending {
  count: number;
  dids: { did_id: number; phone: string }[];
}

export async function listQuarantinePending(): Promise<QuarantinePending> {
  const resp = await api.get<APIResponse<QuarantinePending>>("/dids/quarantine/pending");
  return unwrap(resp.data);
}

export interface PurgeResult {
  did_id: number;
  phone: string;
  status: "deleted" | "failed";
  detail?: string;
}

export async function purgeQuarantinedDid(didId: number): Promise<PurgeResult> {
  const resp = await api.post<APIResponse<PurgeResult>>(`/dids/${didId}/purge`);
  return unwrap(resp.data);
}

export async function getDidReputation(didId: number): Promise<DidReputation> {
  const resp = await api.get<APIResponse<DidReputation>>(`/dids/${didId}/reputation`);
  return unwrap(resp.data);
}

export async function clearDidBlocks(didId: number): Promise<void> {
  await api.post(`/dids/${didId}/clear-blocks`);
}

// ── Caller-ID groups ──────────────────────────────────────────────────────

export async function listDidGroups(): Promise<DidGroup[]> {
  const resp = await api.get<APIResponse<DidGroup[]>>("/dids/groups");
  return unwrap(resp.data);
}

export async function getDidGroup(groupId: number): Promise<DidGroupDetail> {
  const resp = await api.get<APIResponse<DidGroupDetail>>(`/dids/groups/${groupId}`);
  return unwrap(resp.data);
}

export async function createDidGroup(name: string, ownerUsrId?: number): Promise<number> {
  const resp = await api.post<APIResponse<{ did_group_id: number }>>("/dids/groups", {
    name,
    ...(ownerUsrId != null ? { owner_usr_id: ownerUsrId } : {}),
  });
  return unwrap(resp.data).did_group_id;
}

export async function updateDidGroup(
  groupId: number,
  name: string,
  forwarding?: {
    inbound_action_type?: string;
    inbound_action_data?: string;
    inbound_mode?: string;
  },
): Promise<void> {
  await api.put(`/dids/groups/${groupId}`, { name, ...(forwarding ?? {}) });
}

export async function updateDidGroupForwarding(
  groupId: number,
  body: {
    action_type?: "agents" | "external" | "sip";
    action_data?: string;
    forward_to?: string;
    inbound_mode?: "direct" | "press1" | "press3";
  },
): Promise<{
  did_group_id: number;
  action_type: string;
  action_data: string;
  inbound_mode: string;
  updated_dids: number;
}> {
  const resp = await api.put<
    APIResponse<{
      did_group_id: number;
      action_type: string;
      action_data: string;
      inbound_mode: string;
      updated_dids: number;
    }>
  >(`/dids/groups/${groupId}/forwarding`, body);
  return unwrap(resp.data);
}

export async function clearDidGroupBlocks(
  groupId: number,
): Promise<{ cleared: number; dids: number }> {
  const resp = await api.post<APIResponse<{ cleared: number; dids: number }>>(
    `/dids/groups/${groupId}/clear-blocks`,
  );
  return unwrap(resp.data);
}

export async function deleteDidGroup(groupId: number): Promise<void> {
  await api.delete(`/dids/groups/${groupId}`);
}

export interface ReleaseQueued {
  queued: number;
  job_id?: number;
  message?: string;
}

/** Mirrors did_release_job — live progress for a group/quarantined release. */
export interface ReleaseJob {
  job_id: number;
  did_group_id: number;
  created_by: number;
  quarantined_only: boolean;
  status: "pending" | "processing" | "complete" | "failed";
  total: number;
  done: number;
  released: number;
  failed: number;
  started_at: string | null;
  finished_at: string | null;
}

/** Release the whole group: disconnect every number at the carrier, then mark
 *  released (removed from the system + billing + re-use). Irreversible. */
export async function releaseDidGroup(groupId: number): Promise<ReleaseQueued> {
  const resp = await api.post<APIResponse<ReleaseQueued>>(`/dids/groups/${groupId}/release`);
  return unwrap(resp.data);
}

/** Release all QUARANTINED DIDs in the group: disconnect at the carrier + mark
 *  gone (removed from the system/billing/re-use). Irreversible. */
export async function removeQuarantinedFromGroup(groupId: number): Promise<ReleaseQueued> {
  const resp = await api.post<APIResponse<ReleaseQueued>>(
    `/dids/groups/${groupId}/remove-quarantined`,
  );
  return unwrap(resp.data);
}

/** Poll progress of a running release job (drives the UI progress bar). */
export async function getReleaseJob(jobId: number): Promise<ReleaseJob> {
  const resp = await api.get<APIResponse<ReleaseJob>>(`/dids/groups/release-jobs/${jobId}`);
  return unwrap(resp.data);
}

export async function addGroupMembers(groupId: number, didIds: number[]): Promise<number> {
  const resp = await api.post<APIResponse<{ added: number }>>(
    `/dids/groups/${groupId}/members`,
    { did_ids: didIds },
  );
  return unwrap(resp.data).added;
}

export async function removeGroupMember(groupId: number, didId: number): Promise<void> {
  await api.delete(`/dids/groups/${groupId}/members/${didId}`);
}

export async function removeGroupMembersBulk(groupId: number, didIds: number[]): Promise<number> {
  const resp = await api.post<APIResponse<{ removed: number }>>(
    `/dids/groups/${groupId}/members/remove`,
    { did_ids: didIds },
  );
  return unwrap(resp.data).removed;
}

// ── Volume-based DID buying ───────────────────────────────────────────────

export interface BuyPlanRow {
  npa: string; state: string; volume: number; usable: number;
  current_ratio: number | null; target: number; to_buy: number; est_monthly_cost: number;
}
export interface BuyPlan {
  group_id: number; ceiling: number; window_days: number;
  total_to_buy: number; npas_over: number; current_total: number; target_total: number;
  est_monthly_cost: number; monthly_rate: number; per_npa_cap: number;
  dry_run: boolean; rows: BuyPlanRow[];
}
export async function getBuyPlan(groupId: number, ceiling = 50, windowDays = 14): Promise<BuyPlan> {
  const resp = await api.get<APIResponse<BuyPlan>>(
    `/dids/buy-plan?group_id=${groupId}&ceiling=${ceiling}&window_days=${windowDays}`);
  return unwrap(resp.data);
}
export interface BuyExecResult {
  npa: string; requested: number; bought: number; shortfall: number;
  status: string; would_buy?: number;
}
export async function executeBuyNpa(groupId: number, npa: string, qty: number): Promise<BuyExecResult> {
  const resp = await api.post<APIResponse<BuyExecResult>>(
    "/dids/buy-plan/execute", { group_id: groupId, npa, qty });
  return unwrap(resp.data);
}

// ── Campaign-Volume DID Pool Expansion Proposals & One-Click Client Approval ──

export interface ExpansionCampaignItem {
  campaign_id: number;
  name: string;
  status: string;
  channel: number;
  contact_group_id: number | null;
  group_contact_count: number;
  pending_contacts: number;
}

export interface ExpansionNpaRow {
  npa: string;
  state: string;
  est_daily_calls: number;
  pending_contacts: number;
  calls_7d_daily: number;
  usable_dids: number;
  target_dids: number;
  raw_shortfall: number;
  to_buy: number;
  est_cost: number;
}

export interface CampaignExpansionProposal {
  group_id: number;
  group_name: string;
  owner_usr_id: number;
  owner_username: string;
  campaigns: ExpansionCampaignItem[];
  total_pending_contacts: number;
  total_calls_7d: number;
  active_cdr_days: number;
  est_daily_calls: number;
  safe_daily_cap: number;
  current_usable_dids: number;
  burned_or_retired_dids: number;
  recommended_total_dids: number;
  raw_total_shortfall: number;
  total_to_buy: number;
  npas_needing_expansion: number;
  npa_breakdown: ExpansionNpaRow[];
  unit_rate: number;
  billing_unit: string;
  rate_label: string;
  est_cost: number;
  est_cost_label: string;
  user_credit_balance: number;
  can_afford: boolean;
  dry_run: boolean;
}

export interface CampaignExpansionProposalsResponse {
  safe_daily_cap: number;
  did_max_calls_per_hour: number;
  did_max_calls_per_day: number;
  did_soft_cap_hourly: number;
  did_soft_cap_daily: number;
  proposals_needing_action: number;
  total_recommended_to_buy: number;
  proposals: CampaignExpansionProposal[];
}

export async function getCampaignExpansionProposals(
  safeDailyCap = 10,
  maxTotalPerGroup = 500,
): Promise<CampaignExpansionProposalsResponse> {
  const resp = await api.get<APIResponse<CampaignExpansionProposalsResponse>>(
    "/dids/campaign-expansion-proposals",
    { params: { safe_daily_cap: safeDailyCap, max_total_per_group: maxTotalPerGroup } },
  );
  return unwrap(resp.data);
}

export interface ApproveExpansionResult {
  group_id: number;
  group_name: string;
  requested_total: number;
  bought_total: number;
  would_buy_total?: number;
  shortfall_total: number;
  status: string;
  order_ids?: string[];
  rate_label?: string;
  billing_unit?: string;
  est_cost?: number;
  est_cost_label?: string;
  by_npa: {
    npa: string;
    state?: string;
    requested: number;
    found_exact?: number;
    found_state_fallback?: number;
    queued_to_order?: number;
    bought?: number;
    status?: string;
  }[];
}

export async function approveCampaignExpansion(body: {
  group_id: number;
  max_total?: number;
  items?: { npa: string; qty: number }[];
}): Promise<ApproveExpansionResult> {
  const resp = await api.post<APIResponse<ApproveExpansionResult>>(
    "/dids/campaign-expansion-approve",
    body,
  );
  return unwrap(resp.data);
}

// ── Contact-List-Based DID Overage Planner & Chunked AJAX Purchase Runner ──

export interface ContactListSummary {
  contact_group_id: number;
  name: string;
  description: string | null;
  contact_count: number;
  status: string;
  created_by: number | null;
}

export interface ListOverageNpaRow {
  npa: string;
  state: string;
  contacts: number;
  active_dids: number;
  current_leads_per_did: number | null;
  base_needed: number;
  overage_extra: number;
  target_dids: number;
  to_buy: number;
  est_cost: number;
}

export interface ListOveragePlan {
  contact_group_id: number;
  contact_group_name: string;
  did_group_id: number;
  did_group_name: string;
  owner_usr_id: number;
  total_contacts: number;
  existing_active_dids: number;
  existing_matching_dids: number;
  target_total_dids: number;
  raw_total_to_buy: number;
  total_to_buy: number;
  npas_needing_dids: number;
  leads_per_did: number;
  overage_pct: number;
  min_dids_per_npa: number;
  unit_rate: number;
  billing_unit: string;
  rate_label: string;
  est_cost: number;
  est_cost_label: string;
  user_credit_balance: number;
  can_afford: boolean;
  requires_chunking: boolean;
  recommended_chunk_size: number;
  estimated_chunks: number;
  rows: ListOverageNpaRow[];
}

export interface ListOverageChunk {
  chunk_index: number;
  qty: number;
  items: { npa: string; state: string; qty: number }[];
  npa_summary: string;
  status: "pending" | "running" | "completed" | "dry_run" | "cancelled" | "failed";
  bought: number;
  shortfall: number;
  cost: number;
  numbers: string[];
  error?: string;
  completed_at?: string;
}

export interface ListOverageJob {
  job_id: string;
  status: "pending" | "running" | "completed" | "cancelled" | "failed";
  owner_usr_id: number;
  did_group_id: number;
  did_group_name: string;
  contact_group_id: number | null;
  contact_group_name: string | null;
  leads_per_did: number;
  overage_pct: number;
  simulate_provision: boolean;
  unit_rate: number;
  billing_unit: string;
  rate_label: string;
  upfront_rate: number;
  chunk_size: number;
  requires_chunking: boolean;
  total_requested: number;
  total_processed: number;
  total_bought: number;
  total_shortfall: number;
  total_cost: number;
  progress_pct: number;
  chunks_total: number;
  chunks_completed: number;
  current_chunk_index: number;
  current_npa: string | null;
  chunks: ListOverageChunk[];
  purchased_phones: string[];
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

export async function listContactListsForOverage(didGroupId?: number): Promise<ContactListSummary[]> {
  const resp = await api.get<APIResponse<ContactListSummary[]>>("/dids/contact-lists", {
    params: didGroupId ? { did_group_id: didGroupId } : undefined,
  });
  return unwrap(resp.data);
}

export async function getListOveragePlan(params: {
  contact_group_id: number;
  did_group_id: number;
  leads_per_did?: number;
  overage_pct?: number;
  min_dids_per_npa?: number;
  max_total?: number;
}): Promise<ListOveragePlan> {
  const resp = await api.get<APIResponse<ListOveragePlan>>("/dids/list-overage-plan", { params });
  return unwrap(resp.data);
}

export async function createListOverageJob(body: {
  did_group_id: number;
  contact_group_id?: number;
  leads_per_did?: number;
  overage_pct?: number;
  chunk_size?: number;
  items: { npa: string; qty: number }[];
  simulate_provision?: boolean;
}): Promise<ListOverageJob> {
  const resp = await api.post<APIResponse<ListOverageJob>>("/dids/list-overage-jobs", body);
  return unwrap(resp.data);
}

export async function getListOverageJob(jobId: string): Promise<ListOverageJob> {
  const resp = await api.get<APIResponse<ListOverageJob>>(`/dids/list-overage-jobs/${jobId}`);
  return unwrap(resp.data);
}

export async function processListOverageJobChunk(jobId: string): Promise<ListOverageJob> {
  const resp = await api.post<APIResponse<ListOverageJob>>(
    `/dids/list-overage-jobs/${jobId}/chunk`,
  );
  return unwrap(resp.data);
}

export async function cancelListOverageJob(jobId: string): Promise<ListOverageJob> {
  const resp = await api.post<APIResponse<ListOverageJob>>(
    `/dids/list-overage-jobs/${jobId}/cancel`,
  );
  return unwrap(resp.data);
}
